import {
  ChevronDownIcon,
  Cog6ToothIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { Client } from "@stomp/stompjs";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SockJS from "sockjs-client";

import HOWLRLOGO from "../assets/images/hoowlrLogo.svg";
import HOWLRLOGOTwo from "../assets/images/logo.svg";

import homeIcon from "../assets/icons/homeIcon.svg";
import athletesIcon from "../assets/icons/athletesIcon.svg";
import marketplaceIcon from "../assets/icons/marketIcon.svg";
import eventsIcon from "../assets/icons/eventsIcon.svg";
import chatIcon from "../assets/icons/chatIcon.svg";
import historyIcon from "../assets/icons/historyIcon.svg";
import myDealsIcon from "../assets/icons/mydealIcon.svg";
import feedIcon from "../assets/icons/feedIcon.svg";
import notificationIcon from "../assets/icons/notificationIcon.svg";
import knowledgeHubIcon from "../assets/icons/knowledgeHubIcon.svg";
import personIcon from "../assets/icons/personIcon.svg";

import {
  CHAT_WEBSOCKET_URL,
  getContactedCount,
  logoutUser,
} from "../services/api";

import { useNotifications } from "../features/notifications/notificationsContext";
import { Profile } from "../types/types";
import { isValidUrl } from "../utils/utilMethods";

interface SidebarProps {
  isExpanded: boolean;
  isMobileOpen?: boolean;
  hasTopBanner?: boolean;
  onMobileClose?: () => void;
  onDesktopMouseEnter?: () => void;
}

interface SidebarIconProps {
  src: string;
  width: number;
  height: number;
}

interface MenuItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  onClick: () => void;
  badge?: number | null;
  showUnreadDot?: boolean;
}

const WEBSOCKET_URL = CHAT_WEBSOCKET_URL;
const MESSAGE_COUNT_TOPIC = "/user/{USER_ID}/message-count";

const iconSizes = {
  home: { width: 24, height: 28 },
  athletes: { width: 32, height: 24 },
  marketplace: { width: 24, height: 24 },
  events: { width: 24, height: 28 },
  chat: { width: 28, height: 28 },
  history: { width: 24, height: 24 },
  myDeals: { width: 24, height: 28 },
  feed: { width: 28, height: 20 },
  notifications: { width: 24, height: 28 },
  knowledgeHub: { width: 24, height: 24 },
};

const getSavedUser = () => {
  try {
    const userJson = localStorage.getItem("loginResponse");

    return userJson ? JSON.parse(userJson) : null;
  } catch (error) {
    return null;
  }
};

const formatRole = (role?: string | null): string => {
  if (!role) {
    return "User";
  }

  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
};

const SidebarIcon = ({ src, width, height }: SidebarIconProps) => {
  return (
    <span
      className="inline-block shrink-0 bg-current"
      style={{
        width,
        height,
        WebkitMaskImage: `url(${src})`,
        maskImage: `url(${src})`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        WebkitMaskSize: "contain",
        maskSize: "contain",
      }}
    />
  );
};

export const Sidebar: React.FC<SidebarProps> = ({
  isExpanded,
  isMobileOpen = false,
  hasTopBanner = false,
  onMobileClose,
  onDesktopMouseEnter,
}) => {
  const [userProfile, setUserProfile] = useState<Profile | null>(
    getSavedUser()
  );
  const [userProfileType, setUserProfileType] = useState<string | null>(
    getSavedUser()?.profileType || null
  );
  const [showLogoutMenu, setShowLogoutMenu] = useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false);
  const [contactedCount, setContactedCount] = useState<number>(0);

  const { t, i18n } = useTranslation("sidebar");
  const { unreadCount } = useNotifications();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const stompClientRef = useRef<Client | null>(null);
  const wsConnectedRef = useRef<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  const verified = Boolean(getSavedUser()?.verified);

  // On mobile, force expanded appearance; on desktop use hover state
  const effectivelyExpanded = isMobileOpen || isExpanded;

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    if (!isExpanded && !isMobileOpen) {
      setShowLogoutMenu(false);
    }
  }, [isExpanded, isMobileOpen]);

  const fetchContactedCount = async (): Promise<void> => {
    try {
      const count = await getContactedCount();
      setContactedCount(count);
    } catch (error) {
      setContactedCount(0);
    }
  };

  const connectWebSocket = (): void => {
    const currentUser = getSavedUser();

    if (!currentUser?.id) {
      return;
    }

    const client = new Client({
      webSocketFactory: () => new SockJS(WEBSOCKET_URL),

      connectHeaders: {
        Authorization: `Bearer ${currentUser.token || ""}`,
      },

      debug: () => { },

      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.onConnect = () => {
      wsConnectedRef.current = true;

      const messageCountTopic = MESSAGE_COUNT_TOPIC.replace(
        "{USER_ID}",
        currentUser.id.toString()
      );

      client.subscribe(messageCountTopic, () => fetchContactedCount());

      client.subscribe(`/specific/chat/user/${currentUser.id}`, () =>
        fetchContactedCount()
      );

      client.subscribe(`/all/chat/user/${currentUser.id}`, () =>
        fetchContactedCount()
      );

      client.subscribe(`/user/${currentUser.id}/reply`, () =>
        fetchContactedCount()
      );
    };

    client.onStompError = () => {
      wsConnectedRef.current = false;
    };

    client.onWebSocketError = () => {
      wsConnectedRef.current = false;
    };

    client.onDisconnect = () => {
      wsConnectedRef.current = false;
    };

    client.activate();

    stompClientRef.current = client;
  };

  const disconnectWebSocket = (): void => {
    if (stompClientRef.current) {
      stompClientRef.current.deactivate();
      stompClientRef.current = null;
    }

    wsConnectedRef.current = false;
  };

  useEffect(() => {
    const savedUser = getSavedUser();

    if (savedUser) {
      setUserProfile(savedUser);
      setUserProfileType(savedUser.profileType);
    }
  }, []);

  useEffect(() => {
    fetchContactedCount();
    connectWebSocket();

    const fallbackInterval = setInterval(() => {
      if (!wsConnectedRef.current) {
        fetchContactedCount();
      }
    }, 60000);

    return () => {
      clearInterval(fallbackInterval);
      disconnectWebSocket();
    };
  }, [userProfileType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowLogoutMenu(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowLogoutMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchContactedCount();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [userProfileType]);

  const handleProfileClick = (): void => {
    setShowLogoutMenu((prev) => !prev);
  };

  const handleViewProfile = (): void => {
    if (userProfile?.id) {
      setShowLogoutMenu(false);
      onMobileClose?.();
      navigate(`/profile?profileId=${userProfile.id}`);
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      setShowLogoutMenu(false);
      onMobileClose?.();
      disconnectWebSocket();
      await logoutUser();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      localStorage.clear();
      navigate("/");
    }
  };

  const handleImageError = (): void => {
    setImageError(true);
  };

  const handleSettingsClick = (): void => {
    setShowLogoutMenu(false);
    onMobileClose?.();
    navigate("/settings");
  };

  const handleChatClick = (): void => {
    navigate("/chat");
    fetchContactedCount();
    onMobileClose?.();
  };

  const makeItem = (
    name: string,
    path: string,
    icon: React.ReactNode,
    onClick?: () => void
  ): MenuItem => ({
    name,
    path,
    icon,
    onClick: onClick || (() => navigate(path)),
  });

  const homeItem = makeItem(
    t("home"),
    "/dashboard",
    <SidebarIcon src={homeIcon} {...iconSizes.home} />
  );

  const profileListItem = makeItem(
    t("profileList"),
    "/profile-list",
    <SidebarIcon src={athletesIcon} {...iconSizes.athletes} />
  );

  const marketplaceItem = makeItem(
    t("marketplaceCards"),
    "/marketplaceCards",
    <SidebarIcon src={marketplaceIcon} {...iconSizes.marketplace} />
  );

  const eventsItem = makeItem(
    t("events"),
    "/events",
    <SidebarIcon src={eventsIcon} {...iconSizes.events} />
  );

  const chatItem: MenuItem = {
    ...makeItem(
      t("chat"),
      "/chat",
      <SidebarIcon src={chatIcon} {...iconSizes.chat} />,
      handleChatClick
    ),
    badge: contactedCount > 0 ? contactedCount : null,
  };

  const historyItem = makeItem(
    t("history"),
    "/history",
    <SidebarIcon src={historyIcon} {...iconSizes.history} />
  );

  const myDealsItem = makeItem(
    t("mydeals"),
    "/mydeals",
    <SidebarIcon src={myDealsIcon} {...iconSizes.myDeals} />
  );

  const feedsItem = makeItem(
    t("feeds"),
    "/feeds",
    <SidebarIcon src={feedIcon} {...iconSizes.feed} />
  );

  const notificationsItem: MenuItem = {
    ...makeItem(
      t("notifications"),
      "/notifications",
      <SidebarIcon src={notificationIcon} {...iconSizes.notifications} />
    ),
    showUnreadDot: unreadCount > 0,
  };

  const knowledgeHubItem = makeItem(
    t("knowledgeHub"),
    "/knowledge-hub",
    <SidebarIcon src={knowledgeHubIcon} {...iconSizes.knowledgeHub} />
  );

  const isManager =
    userProfileType === "MANAGER" || userProfileType === "ORGANIZATION";

  const menuItems: MenuItem[] = [
    homeItem,
    profileListItem,
    marketplaceItem,
    ...(isManager ? [eventsItem] : []),
    chatItem,
    historyItem,
    myDealsItem,
    feedsItem,
    notificationsItem,
    knowledgeHubItem,
  ];

  const isActiveRoute = (path: string): boolean => {
    return location.pathname === path;
  };

  const getItemClass = (isActive: boolean): string => {
    const colorClass = isActive
      ? "bg-[#DBEAFE] text-[#2563EB]"
      : "text-[#525252] hover:text-[#2563EB] active:text-[#1E40AF]";

    return `relative flex h-12 w-full shrink-0 cursor-pointer items-center gap-2 overflow-visible rounded-lg p-2 transition-colors duration-200 ${colorClass}`;
  };

  const fadeClass = effectivelyExpanded
    ? "opacity-100 duration-200 delay-150"
    : "opacity-0 duration-75";

  const labelClass = `min-w-0 truncate whitespace-nowrap transition-opacity ${fadeClass}`;

  const imageUrl = userProfile?.profileImage || userProfile?.profilePicture;
  const showProfileImage = !imageError && imageUrl && isValidUrl(imageUrl);

  const isDropdownOpen = showLogoutMenu;

  return (
    <aside
      onMouseEnter={onDesktopMouseEnter}
      className={`fixed left-0 top-0 z-40 flex h-screen flex-col overflow-visible bg-white px-4 py-6 font-sans shadow-[inset_-1px_0_0_#DEDEE0] transition-[width,transform] duration-300 ease-in-out
        w-[280px] ${effectivelyExpanded ? "lg:w-[324px]" : "lg:w-[80px]"}
        ${hasTopBanner ? "lg:top-10 lg:h-[calc(100vh-40px)]" : ""}
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
    >
      <Link
        to="/dashboard"
        onClick={() => onMobileClose?.()}
        aria-label="Hoowlr"
        className={`block relative h-12 shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out ${
          effectivelyExpanded ? "w-[153px]" : "w-10"
        }`}
      >
        <img
          src={HOWLRLOGO}
          alt="Hoowlr"
          className={`absolute left-0 top-0 h-12 w-10 max-w-none object-contain transition-opacity duration-200 ${
            effectivelyExpanded ? "opacity-0" : "opacity-100"
          }`}
        />
        <img
          src={HOWLRLOGOTwo}
          alt=""
          className={`absolute left-0 top-0 h-12 w-[153px] max-w-none object-contain transition-opacity duration-200 ${
            effectivelyExpanded ? "opacity-100" : "opacity-0"
          }`}
        />
      </Link>

      <nav className="mt-12 flex-1 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex flex-col gap-2 [@media(min-height:850px)]:gap-4">
          {menuItems.map((item) => {
            const isActive = isActiveRoute(item.path);

            return (
              <div
                key={item.path + item.name}
                onClick={() => {
                  item.onClick();
                  onMobileClose?.();
                }}
                className={getItemClass(isActive)}
              >
                <span className="relative flex h-8 w-8 shrink-0 items-center justify-center">
                  {item.icon}

                  {item.showUnreadDot && (
                    <span className="absolute right-0.5 top-0.5 h-2.5 w-2.5 rounded-full bg-[#EF4444] ring-2 ring-white" />
                  )}

                  {!effectivelyExpanded && item.badge && (
                    <span className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#2563EB] px-1 text-[10px] font-bold leading-none text-white shadow-sm ring-2 ring-white select-none">
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </span>

                <span className={`${labelClass} text-base font-medium leading-6`}>
                  {item.name}
                </span>

                {effectivelyExpanded && item.badge && (
                  <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#2563EB] px-1.5 text-[11px] font-bold leading-none text-white shadow-sm select-none">
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      {/* User profile section at the bottom */}
      <div className="relative shrink-0 pt-4" ref={dropdownRef}>
        <div
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={isDropdownOpen}
          onClick={handleProfileClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleProfileClick();
            }
          }}
          className="flex h-12 w-full cursor-pointer select-none items-center focus-visible:outline-none"
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center p-2">
            <div
              className={`relative flex h-8 w-8 items-center justify-center rounded-full bg-[#EBEBEC] transition-all duration-200 ${
                isDropdownOpen ? "ring-2 ring-blue-600" : ""
              }`}
            >
              {showProfileImage ? (
                <img
                  src={imageUrl}
                  alt={`${userProfile?.firstName || "User"} ${
                    userProfile?.lastName || ""
                  }`}
                  className="h-8 w-8 rounded-full object-cover"
                  onError={handleImageError}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <img src={personIcon} alt="Profile" className="h-4 w-4" />
              )}

              {verified && (
                <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 overflow-hidden rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 48 48"
                    className="h-full w-full"
                  >
                    <polygon
                      fill="#42a5f5"
                      points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884"
                    />
                    <polygon
                      fill="#fff"
                      points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926"
                    />
                  </svg>
                </span>
              )}
            </div>
          </div>

          <div className={`min-w-0 flex-1 ${labelClass}`}>
            <p className="truncate text-base font-normal leading-6 text-[#18181B]">
              {userProfile
                ? `${userProfile.firstName || "User"} ${
                    userProfile.lastName || ""
                  }`
                : "Loading..."}
            </p>

            <p className="truncate text-sm leading-5 text-[#71717A]">
              {formatRole(userProfile?.profileType)}
            </p>
          </div>

          <ChevronDownIcon
            className={`mr-2 h-6 w-6 shrink-0 text-[#18181B] transition-[transform,opacity] ${
              isDropdownOpen ? "rotate-180" : ""
            } ${fadeClass}`}
          />
        </div>

        {/* Profile Dropdown Menu */}
        <div
          role="menu"
          aria-hidden={!isDropdownOpen}
          className={`w-72 p-1 absolute bottom-[calc(100%+8px)] left-0 bg-white rounded-3xl border border-gray-100 shadow-[0px_14px_28px_0px_rgba(0,0,0,0.08),0px_2px_8px_0px_rgba(0,0,0,0.06)] flex flex-col gap-0.5 z-50 transition-all duration-200 ease-out ${
            isDropdownOpen
              ? "translate-y-0 opacity-100 pointer-events-auto"
              : "translate-y-2 opacity-0 pointer-events-none"
          }`}
        >
          <div className="flex flex-col">
            {/* View Profile */}
            <button
              type="button"
              role="menuitem"
              tabIndex={isDropdownOpen ? 0 : -1}
              onClick={handleViewProfile}
              className="min-h-9 px-3 py-2 rounded-[20px] flex items-center gap-3 text-left transition-colors duration-150 hover:bg-gray-100 text-gray-800 group"
            >
              <div className="size-4 shrink-0 flex items-center justify-center text-gray-500 group-hover:text-blue-600 transition-colors">
                <UserIcon className="w-4 h-4" />
              </div>
              <div className="flex-1 truncate">
                <span className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-900 leading-5">
                  {t("profile", "Profile")}
                </span>
              </div>
            </button>

            {/* Account Settings */}
            <button
              type="button"
              role="menuitem"
              tabIndex={isDropdownOpen ? 0 : -1}
              onClick={handleSettingsClick}
              className="min-h-9 px-3 py-2 rounded-[20px] flex items-center gap-3 text-left transition-colors duration-150 hover:bg-gray-100 text-gray-800 group"
            >
              <div className="size-4 shrink-0 flex items-center justify-center text-gray-500 group-hover:text-blue-600 transition-colors">
                <Cog6ToothIcon className="w-4 h-4" />
              </div>
              <div className="flex-1 truncate">
                <span className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-900 leading-5">
                  {t("accountSettings", "Account settings")}
                </span>
              </div>
            </button>
          </div>

          {/* Divider */}
          <div className="self-stretch px-3 py-1">
            <div className="h-px bg-gray-100 w-full" />
          </div>

          {/* Logout */}
          <div className="flex flex-col">
            <button
              type="button"
              role="menuitem"
              tabIndex={isDropdownOpen ? 0 : -1}
              onClick={handleLogout}
              className="min-h-9 px-3 py-2 rounded-[20px] flex items-center gap-3 text-left transition-colors duration-150 hover:bg-red-50 text-red-600 group"
            >
              <div className="flex-1 truncate">
                <span className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-red-600 leading-5">
                  {t("logout", "Logout")}
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};
